import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStoreAuth } from "../StoreAuthContext";
import StoreSignIn from "./StoreSignIn";
import { isStoreArabicLang } from "../../../utils/storeUtils";

function getInitials(text) {
  if (!text) return "";
  const parts = String(text).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("");
}

export default function StoreProfileMenu({ buttonClassName = "" }) {
  const { t, i18n } = useTranslation("global");
  const isRtl = isStoreArabicLang(i18n.language);
  const { user, loading, isAuthenticated, logout } = useStoreAuth();
  const [open, setOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  // On small screens the menu is rendered through a body portal with this
  // fixed-position style, so no header element can overlap or clip it.
  const [mobileMenuStyle, setMobileMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const location = useLocation();

  const displayName = user?.fullName || user?.email || "";
  // Support both avatar and picture (Google returns picture)
  const avatarUrl = user?.avatar || user?.picture || null;
  const showAvatar = avatarUrl && !avatarError;

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        const isMobile = window.matchMedia("(max-width: 640px)").matches;
        if (isMobile) {
          const rect = rootRef.current?.getBoundingClientRect();
          setMobileMenuStyle({
            position: "fixed",
            top: Math.round((rect?.bottom ?? 56) + 10),
            left: 10,
            right: 10,
            width: "auto",
            minWidth: 0,
            maxHeight: "72dvh",
            overflowY: "auto",
            zIndex: 2147483000,
          });
        } else {
          setMobileMenuStyle(null);
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    const onPointerDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open, close]);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  // Reset avatar error when user or avatar URL changes (e.g. after login)
  useEffect(() => {
    setAvatarError(false);
  }, [user?._id, avatarUrl]);

  const buttonClass = ["store-profile-button", buttonClassName].filter(Boolean).join(" ");

  return (
    <div className="store-profile" ref={rootRef}>
      <button
        type="button"
        className={buttonClass}
        aria-label={
          isAuthenticated
            ? t("header.storeProfileOpenMenu", { defaultValue: "Open profile menu" })
            : t("header.storeProfileOpenSignIn", { defaultValue: "Open sign-in menu" })
        }
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        {showAvatar ? (
          <img
            src={avatarUrl}
            alt=""
            className="store-profile-avatar"
            width={32}
            height={32}
            referrerPolicy="no-referrer"
            onError={() => setAvatarError(true)}
          />
        ) : displayName ? (
          <span className="store-profile-initials" aria-hidden>
            {getInitials(displayName)}
          </span>
        ) : (
          <svg
            viewBox="0 0 24 24"
            className="store-profile-icon"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z"
            />
          </svg>
        )}
      </button>

      {open && renderMenu(
        <div
          ref={menuRef}
          className="store-profile-menu"
          role="menu"
          dir={isRtl ? "rtl" : "ltr"}
          lang={isRtl ? "ar" : "en"}
          style={mobileMenuStyle || undefined}
          aria-label={t("header.storeProfileMenuAria", { defaultValue: "Account menu" })}
        >
          {loading ? (
            <div className="store-profile-row store-profile-muted">
              {t("header.storeProfileLoading", { defaultValue: "Loading…" })}
            </div>
          ) : isAuthenticated ? (
            <>
              <div className="store-profile-row">
                <div className="store-profile-name">{displayName}</div>
                {user?.email && user?.fullName ? (
                  <div className="store-profile-email" dir="ltr">
                    {user.email}
                  </div>
                ) : null}
              </div>
              <div className="store-profile-divider" />
              <Link className="store-profile-link" to="/store/my-orders" role="menuitem">
                {t("header.storeNavOrders", { defaultValue: "My orders" })}
              </Link>
              <Link className="store-profile-link" to="/store/wishlist" role="menuitem">
                {t("header.storeNavWishlist", { defaultValue: "Wishlist" })}
              </Link>
              <Link className="store-profile-link" to="/store/checkout" role="menuitem">
                {t("header.storeNavCheckout", { defaultValue: "Checkout" })}
              </Link>
              <button
                type="button"
                className="store-profile-logout"
                onClick={() => {
                  logout();
                  close();
                }}
              >
                {t("header.storeProfileSignOut", { defaultValue: "Sign out" })}
              </button>
            </>
          ) : (
            <>
              <div className="store-profile-row">
                <div className="store-profile-title">
                  {t("header.storeProfileSignInTitle", { defaultValue: "Sign in" })}
                </div>
                <div className="store-profile-muted">
                  {t("header.storeProfileSignInHint", {
                    defaultValue: "Use your Google account.",
                  })}
                </div>
              </div>
              <div className="store-profile-divider" />
              <div className="store-profile-signin">
                <StoreSignIn />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  // Mobile: portal to <body> so the menu paints above the search bar and
  // every other header element, whatever their stacking contexts are.
  function renderMenu(node) {
    return mobileMenuStyle ? createPortal(node, document.body) : node;
  }
}
